import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/kit";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, ShieldCheck } from "lucide-react";

const ACTIONS = [
  "DATA_RESET", "SALARY_UPDATE", "EMPLOYEE_CREATE", "EMPLOYEE_UPDATE", "EMPLOYEE_DELETE",
  "EMPLOYEE_ID_CHANGE", "PAYSLIP_CREATE", "PAYSLIP_BULK_CREATE", "PAYSLIP_DELETE",
  "LOAN_APPROVE", "LOAN_REJECT", "LOAN_DISBURSE", "LOAN_EMI_RECORD", "LOAN_DELETE",
  "SESSION_REVOKE_ALL",
  "FACE_REGISTER", "FACE_REENROLL", "FACE_RESET", "FACE_RECHECK_FAIL", "FACE_REMIND", "FACE_APPROVE", "LOAN_SIGNED_COPY",
];

const ACTION_STYLE = {
  DATA_RESET: "bg-red-100 text-red-700 border-red-200",
  EMPLOYEE_DELETE: "bg-red-100 text-red-700 border-red-200",
  PAYSLIP_DELETE: "bg-red-100 text-red-700 border-red-200",
  LOAN_REJECT: "bg-red-100 text-red-700 border-red-200",
  LOAN_DELETE: "bg-red-100 text-red-700 border-red-200",
  LOAN_DISBURSE: "bg-amber-100 text-amber-800 border-amber-200",
  LOAN_APPROVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  SALARY_UPDATE: "bg-amber-100 text-amber-800 border-amber-200",
  PAYSLIP_CREATE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PAYSLIP_BULK_CREATE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  FACE_REGISTER: "bg-emerald-100 text-emerald-800 border-emerald-200",
  FACE_REENROLL: "bg-sky-100 text-sky-800 border-sky-200",
  FACE_RESET: "bg-amber-100 text-amber-800 border-amber-200",
  FACE_RECHECK_FAIL: "bg-red-100 text-red-700 border-red-200",
  FACE_APPROVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  LOAN_SIGNED_COPY: "bg-sky-100 text-sky-800 border-sky-200",
};

const label = (a) => a.replace(/_/g, " ");

const fmtWhen = (ts) => {
  try {
    return new Date(ts).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
};

export default function AuditTrail() {
  const [action, setAction] = useState("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["audit-logs", action],
    queryFn: async () =>
      (await api.get("/audit-logs", { params: action !== "all" ? { action } : {} })).data,
  });

  if (isLoading)
    return (
      <div>
        <PageHeader title="Audit Trail" subtitle="Record of privileged administrative actions" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );

  return (
    <div data-testid="audit-trail-page">
      <PageHeader title="Audit Trail" subtitle="Immutable record of privileged administrative actions">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-56" data-testid="audit-action-filter">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>{label(a)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      <SectionCard title="Activity Log" testId="audit-list">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="audit-empty">
            <div className="rounded-full bg-navy/5 p-4 mb-3"><ShieldCheck className="h-8 w-8 text-navy" /></div>
            <p className="text-sm font-medium text-slate-500">No administrative actions recorded yet</p>
            <p className="mt-1 text-xs text-slate-400">Sensitive actions (salary, loans, payslips, resets) are logged here automatically.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.audit_id} data-testid={`audit-row-${r.audit_id}`}>
                    <TableCell className="whitespace-nowrap text-sm text-slate-700">{fmtWhen(r.timestamp)}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-navy">{r.actor_name || "—"}</div>
                      <div className="text-xs text-slate-400">{r.actor_email} · {r.actor_role}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ACTION_STYLE[r.action] || "bg-slate-100 text-slate-700 border-slate-200"}>
                        {label(r.action)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md text-sm text-slate-600">
                      <span className="text-slate-400">{r.target ? `${r.target} · ` : ""}</span>{r.details}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 font-mono">{r.ip || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
